import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { layout, palette, typography } from '../../utils/designTokens';
import ServiceChips from './components/shared/ServiceChips';
import { formatNumber } from '../../utils/formatters';
import { safelyParseJSON } from '../../utils/json';
import { buildSummaryData, buildSummaryTree } from '../../utils/traceSummaryTree';

const ROW_HEIGHT = 46;
const INDENT_PER_LEVEL = layout.indentPerLevel;

const SpanRow = ({ data, index, style }) => {
  const { rows, selectedPath, onSelect, onToggle, isMultiService, isMultiEnv, evidenceSpanIds } = data;
  const row = rows[index];
  const { span, depth, hasChildren, isExpanded, path } = row;

  const isSelected = selectedPath === path;
  const isError = span.status_code === 'ERROR';
  const isEvidence = evidenceSpanIds?.has?.(span.span_id);

  const attributes = typeof span.attributes === 'string'
    ? safelyParseJSON(span.attributes)
    : span.attributes || {};
  // Check if span has detection data from API (can be multiple detections)
  const hasDetections = Array.isArray(span.detections) && span.detections.length > 0;

  const primaryTextClass = isSelected ? 'text-accent-primary-base' : 'text-text-primary';
  const mutedTextClass = isSelected ? 'text-accent-primary-base/80' : 'text-text-muted';
  const statusDotClass = (isError || hasDetections)
    ? 'bg-danger'
    : isEvidence
      ? 'bg-warning'
    : isSelected
      ? 'bg-accent-primary-base'
      : 'bg-accent-primary-base';
  const timelineBarClass = (isError || hasDetections)
    ? 'bg-danger'
    : isEvidence
      ? 'bg-warning'
    : isSelected
      ? 'bg-accent-primary-base'
      : 'bg-accent-secondary-base';

  const secondaryLabel = (() => {
    const label = span.agent_name || span.tool_name || '';
    return label === span.name ? '' : label;
  })();

  const handleRowClick = () => onSelect(row);
  const handleToggle = (event) => {
    event.stopPropagation();
    onToggle(path);
  };

  const rowFrameClass = `relative z-10 flex w-full items-center rounded-lg border px-2 py-1 transition-colors ${
    isSelected
      ? 'border-accent-primary-base border-l-[3px]'
      : isEvidence
        ? 'border-warning/70 border-l-[3px]'
        : 'border-transparent hover:bg-background-tertiary'
  }`;

  const rowFrameStyle = {
    height: ROW_HEIGHT,
    ...(isSelected
      ? { backgroundColor: palette.selection.bg }
      : isEvidence
        ? { backgroundColor: `color-mix(in srgb, ${palette.status.warning} 8%, transparent)` }
        : {}),
  };

  return (
    <div
      style={style}
      className="relative flex items-center px-2 cursor-pointer"
      onClick={handleRowClick}
    >
      <div className={rowFrameClass} style={rowFrameStyle}>
        <div
          className={`flex items-center gap-2 ${layout.spanColumnWidth}`}
          style={{ paddingLeft: depth * INDENT_PER_LEVEL }}
        >
          {hasChildren ? (
          <button
            type="button"
            onClick={handleToggle}
            className={`w-6 h-6 flex items-center justify-center text-sm transition-colors ${
              isSelected ? 'text-accent-primary-base/80 hover:text-accent-primary-base' : 'text-text-muted hover:text-text-primary'
            }`}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <svg
                className="w-3 h-3"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.25 4.25L6 7.75L9.75 4.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                className="w-3 h-3"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.25 2.25L7.75 6L4.25 9.75"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        ) : (
          <span className="w-4 h-4" />
        )}
        <div className={`w-2 h-2 rounded-full ${statusDotClass}`} />
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span
              className={`text-sm truncate transition-colors ${primaryTextClass}`}
              title={span.name}
            >
              {span.name || 'Unnamed span'}
            </span>
            {isMultiService && span.service_name && (
              <span className="shrink-0 px-1 rounded text-[9px] border border-border-strong text-text-muted" title={isMultiEnv && span.environment ? `${span.service_name} · ${span.environment}` : span.service_name}>
                {isMultiEnv && span.environment ? `${span.service_name} · ${span.environment}` : span.service_name}
              </span>
            )}
            {hasDetections && (
              <svg className="w-3.5 h-3.5 text-danger flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" title="Signal Detection">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            )}
            {isEvidence && (
              <span className="shrink-0 rounded border border-warning/60 px-1 py-0.5 text-[9px] text-warning" title="Classifier evidence span">
                EVIDENCE
              </span>
            )}
          </div>
          {secondaryLabel && (
            <span
              className={`text-xs truncate transition-colors ${mutedTextClass}`}
              title={secondaryLabel}
            >
              {secondaryLabel}
            </span>
          )}
        </div>
      </div>

      <div className={`flex-1 grid ${layout.grid.spanColumns} items-center ${typography.size.sm}`}>
        <span className={`${layout.grid.cellPadding} transition-colors ${mutedTextClass}`}>{span.span_kind || 'SPAN'}</span>
        <span className={`${layout.grid.cellPadding} transition-colors ${primaryTextClass}`}>
          {formatNumber(span.duration_ms)} ms
        </span>
        <span className={`${layout.grid.cellPadding} transition-colors ${primaryTextClass}`}>
          {formatNumber(span.llm_token_count_total || span.token_count)}
        </span>
      </div>

      <div className={`${layout.timelineWidthClass} ${layout.grid.cellPadding}`}>
            <div className="h-2 rounded bg-background-tertiary relative overflow-hidden">
            <div
              className={`absolute top-0 bottom-0 rounded transition-colors ${timelineBarClass}`}
              style={{
                left: `${span.timelinePosition || 0}%`,
                width: `${Math.max(Math.min(span.timelineDuration || 0, 100), 1)}%`,
              }}
            />
        </div>
      </div>
      </div>
    </div>
  );
};


const buildSpanTree = (spans) => {
  if (!Array.isArray(spans)) return [];

  const map = new Map();
  spans.forEach((span) => {
    if (!span?.span_id) return;
    const node = map.get(span.span_id) || { span, children: [] };
    node.span = span;
    map.set(span.span_id, node);
  });

  spans.forEach((span) => {
    if (!span?.span_id) return;
    const parentId = span.parent_span_id;
    if (parentId && map.has(parentId) && map.has(span.span_id)) {
      const parentNode = map.get(parentId);
      const childNode = map.get(span.span_id);
      if (!parentNode.children.includes(childNode)) {
        parentNode.children.push(childNode);
      }
    }
  });

  return spans
    .filter((span) => !span.parent_span_id || !map.has(span.parent_span_id))
    .map((span) => map.get(span.span_id));
};

const flattenTreeWithExpansion = (nodes, expandedPaths, depth = 0, pathPrefix = 'root') => {
  const rows = [];

  nodes.forEach((node, index) => {
    const path = `${pathPrefix}.${index}`;
    const isExpanded = expandedPaths.has(path);
    const hasChildren = node.children.length > 0;

    rows.push({ span: node.span, depth, hasChildren, isExpanded, path });

    if (hasChildren && isExpanded) {
      rows.push(
        ...flattenTreeWithExpansion(node.children, expandedPaths, depth + 1, path)
      );
    }
  });

  return rows;
};

const findSpanPath = (nodes, targetSpanId, pathPrefix = 'root') => {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (!node?.span?.span_id) continue;

    const currentPath = `${pathPrefix}.${index}`;
    if (node.span.span_id === targetSpanId) {
      return {
        targetPath: currentPath,
        ancestorPaths: []
      };
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
      const childResult = findSpanPath(node.children, targetSpanId, currentPath);
      if (childResult) {
        return {
          targetPath: childResult.targetPath,
          ancestorPaths: [currentPath, ...childResult.ancestorPaths]
        };
      }
    }
  }

  return null;
};

const TraceTimeline = ({ traceId, spans: spansProp, trace, selectedSpan, onSpanSelect, expandedPaths, onExpandedPathsChange, filterMode = 'summary', onFilterModeChange, isMultiService = false, isMultiEnv = false, evidenceSpanIds = new Set(), participants = [] }) => {
  const effectiveTraceId = traceId || trace?.trace_id;
  const listOuterRef = useRef(null);
  const listRef = useRef(null);
  const [scrollIndicators, setScrollIndicators] = useState({ canScrollUp: false, canScrollDown: false });

  // Stable across filterMode toggles — only recomputes when spans change
  const summaryData = useMemo(() => buildSummaryData(spansProp || []), [spansProp]);

  // Timeline positioning must use the full span set before filtering so bars are relative to the whole trace
  const spans = useMemo(() => {
    if (!spansProp || spansProp.length === 0) return [];

    const processedSpans = [...spansProp];

    const startTimes = processedSpans.map(s => new Date(s.start_time).getTime()).filter(t => !isNaN(t));
    const endTimes = processedSpans.map(s => new Date(s.end_time).getTime()).filter(t => !isNaN(t));

    if (startTimes.length > 0 && endTimes.length > 0) {
      const traceStartMs = Math.min(...startTimes);
      const traceEndMs = Math.max(...endTimes);
      const traceDurationMs = traceEndMs - traceStartMs;

      processedSpans.forEach(span => {
        const spanStartMs = new Date(span.start_time).getTime();
        const spanDurationMs = span.duration_ms || 0;

        if (!isNaN(spanStartMs) && traceDurationMs > 0) {
          const relativeStartMs = spanStartMs - traceStartMs;
          let timelinePosition = Math.max(0, Math.min(100, (relativeStartMs / traceDurationMs) * 100));
          let timelineDuration = Math.max(0, Math.min(100, (spanDurationMs / traceDurationMs) * 100));

          if (timelinePosition + timelineDuration > 100) {
            timelineDuration = 100 - timelinePosition;
          }

          span.timelinePosition = Math.round(timelinePosition * 100) / 100;
          span.timelineDuration = Math.round(timelineDuration * 100) / 100;
        } else {
          span.timelinePosition = 0;
          span.timelineDuration = 0;
        }
      });
    }

    if (filterMode === 'summary') {
      return processedSpans.filter(s => summaryData.ids.has(s.span_id));
    }

    return processedSpans;
  }, [spansProp, filterMode, summaryData]);


  // In summary mode, use buildSummaryTree directly so promoted parent-child
  // relationships match the Outline view. In all mode, derive hierarchy from parent_span_id.
  const spanTree = useMemo(() => {
    if (filterMode === 'summary') {
      return buildSummaryTree(spansProp || [], { includeAllNodes: false });
    }
    return buildSpanTree(spans);
  }, [filterMode, spansProp, spans]);

  useEffect(() => {
    // Only initialize expansion if we don't have any expanded paths yet
    if (expandedPaths.size > 0) return;

    const initialExpanded = new Set();
    const walk = (nodes, pathPrefix = 'root') => {
      nodes.forEach((node, index) => {
        const path = `${pathPrefix}.${index}`;
        initialExpanded.add(path);
        if (node.children.length > 0) {
          walk(node.children, path);
        }
      });
    };
    walk(spanTree);
    onExpandedPathsChange(initialExpanded);
  }, [spanTree, expandedPaths, onExpandedPathsChange]);

  const flatRows = useMemo(
    () => flattenTreeWithExpansion(spanTree, expandedPaths),
    [spanTree, expandedPaths]
  );

  const updateScrollIndicators = useCallback((nextOffset) => {
    const outer = listOuterRef.current;
    if (!outer) {
      setScrollIndicators({ canScrollUp: false, canScrollDown: false });
      return;
    }

    const viewportHeight = outer.clientHeight;
    const totalHeight = flatRows.length * ROW_HEIGHT;
    const scrollTop = typeof nextOffset === 'number' ? nextOffset : outer.scrollTop;

    if (totalHeight <= viewportHeight) {
      setScrollIndicators({ canScrollUp: scrollTop > 0, canScrollDown: false });
      return;
    }

    const maxOffset = totalHeight - viewportHeight;
    const canScrollUp = scrollTop > 1;
    const canScrollDown = scrollTop < maxOffset - 1;
    setScrollIndicators({ canScrollUp, canScrollDown });
  }, [flatRows.length]);

  const handleSelectSpan = useCallback((row) => {
    onSpanSelect(row.span);
  }, [onSpanSelect]);

  const handleTogglePath = useCallback((path) => {
    onExpandedPathsChange((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, [onExpandedPathsChange]);

  const selectedRow = useMemo(() => {
    if (!selectedSpan) return null;
    return flatRows.find((row) => row.span.span_id === selectedSpan.span_id) || null;
  }, [flatRows, selectedSpan]);

  const selectedPath = selectedRow?.path ?? null;
  const selectedRowIndex = useMemo(() => {
    if (!selectedRow) return -1;
    return flatRows.findIndex((row) => row.path === selectedRow.path);
  }, [flatRows, selectedRow]);

  useEffect(() => {
    if (!flatRows.length) {
      if (selectedSpan) {
        onSpanSelect(null);
      }
      return;
    }

    if (selectedRow) {
      return;
    }

    if (selectedSpan) {
      const pathInfo = findSpanPath(spanTree, selectedSpan.span_id);
      if (pathInfo) {
        onExpandedPathsChange((prev) => {
          let changed = false;
          const next = new Set(prev);
          pathInfo.ancestorPaths.forEach((path) => {
            if (!next.has(path)) {
              next.add(path);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
        return;
      }

      if (filterMode === 'summary') {
        onFilterModeChange('all');
        return;
      }
    }

    const firstRow = flatRows[0];
    if (firstRow) {
      onSpanSelect(firstRow.span);
    }
  }, [flatRows, selectedRow, selectedSpan, onSpanSelect, spanTree, onExpandedPathsChange, filterMode, onFilterModeChange]);

  useEffect(() => {
    updateScrollIndicators();
  }, [flatRows.length, updateScrollIndicators]);

  useEffect(() => {
    if (selectedRowIndex < 0 || !listRef.current) return;
    listRef.current.scrollToItem(selectedRowIndex, 'center');
    requestAnimationFrame(() => updateScrollIndicators());
  }, [selectedRowIndex, selectedSpan?.span_id, updateScrollIndicators]);

  // Single return with nested ternaries - no early returns to comply with Rules of Hooks
  return !effectiveTraceId ? (
    <div className="flex items-center justify-center h-full">
      <p className="text-text-muted text-sm">Select a trace to view details</p>
    </div>
  ) : !spansProp || spansProp.length === 0 ? (
    <div className="flex items-center justify-center h-full">
      <p className="text-text-muted text-sm">No spans available</p>
    </div>
  ) : (
        <div className="flex flex-col h-full">
          <div className="border-b border-accent-lavender/20 bg-background-primary sticky top-0 z-10">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
              <div className="flex items-center gap-3">
                <span className="text-xs text-text-muted">
                  {filterMode === 'summary' ? 'Showing key spans' : 'Showing all spans'}
                </span>
                <ServiceChips participants={participants} />
              </div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-border-subtle bg-background-tertiary hover:bg-background-quaternary text-text-primary"
                onClick={() => onFilterModeChange(filterMode === 'summary' ? 'all' : 'summary')}
              >
                {filterMode === 'summary' ? 'Show All' : 'Show Summary'}
              </button>
            </div>
            <div className={`flex items-center p-3 ${typography.size.metadata} ${typography.weight.semibold} text-text-muted uppercase`}>
              <div className={`${layout.spanColumnWidth} pl-8`}>Span</div>
              <div className={`flex-1 grid ${layout.grid.spanColumns}`}>
                <span className={layout.grid.cellPadding}>Kind</span>
                <span className={layout.grid.cellPadding}>Duration</span>
                <span className={layout.grid.cellPadding}>Tokens</span>
              </div>
              <div className={`${layout.timelineWidthClass} ${layout.grid.cellPadding}`}>Timeline</div>
            </div>
          </div>
          <div className="flex-1 relative">
            {spans.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-text-muted text-sm">No key spans found. Click &ldquo;Show All&rdquo; to view all spans.</p>
              </div>
            ) : (
              <>
                <AutoSizer>
                  {({ height, width }) => (
                    <List
                      ref={listRef}
                      height={height}
                      width={width}
                      itemCount={flatRows.length}
                      itemSize={ROW_HEIGHT}
                      outerRef={listOuterRef}
                      onScroll={({ scrollOffset }) => updateScrollIndicators(scrollOffset)}
                      itemData={{
                        rows: flatRows,
                        selectedPath,
                        onSelect: handleSelectSpan,
                        onToggle: handleTogglePath,
                        isMultiService,
                        isMultiEnv,
                        evidenceSpanIds,
                      }}
                    >
                      {SpanRow}
                    </List>
                  )}
                </AutoSizer>
                {scrollIndicators.canScrollUp && (
                  <div className="pointer-events-none absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-background-primary via-background-primary/60 to-transparent" />
                )}
                {scrollIndicators.canScrollDown && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-background-primary via-background-primary/60 to-transparent" />
                )}
              </>
            )}
          </div>
        </div>
  );
};

export default TraceTimeline;
